import { normalizeSearchText, tokenizeSearchText } from "./searchText.js";
import {
  ARCHIVE_SEARCH_INDEX_SCOPES,
  filterArchiveSearchIndexForScope,
  normalizeArchiveSearchIndexScope,
} from "./archiveSearchScope.js";
import { cleanViewerFacingCopy, cleanViewerFacingCopyList } from "../../utils/viewerFacingCopy.js";

export {
  ARCHIVE_SEARCH_INDEX_SCOPES,
  filterArchiveSearchIndexForScope,
  normalizeArchiveSearchIndexScope,
} from "./archiveSearchScope.js";

const viteEnv = import.meta.env || {};

export const ARCHIVE_SEARCH_INDEX_PATH = viteEnv.VITE_ARCHIVE_SEARCH_INDEX_PATH || "/api/archive-search-index.json";
export const ARCHIVE_SEARCH_INDEX_ALL_PATH = viteEnv.VITE_ARCHIVE_SEARCH_INDEX_ALL_PATH || "/api/archive-search-index-all.json";
export const ARCHIVE_SEARCH_API_INDEX_PATH = viteEnv.VITE_ARCHIVE_SEARCH_API_INDEX_PATH || "/api/v1/archive/index";

const archiveSearchIndexPromises = new Map();

function buildTagLookup(tags = []) {
  return new Map(
    tags
      .filter((tag) => tag?.slug && tag?.tag)
      .map((tag) => [tag.slug, tag])
  );
}

function normalizeTag(tag = {}) {
  const label = cleanViewerFacingCopy(tag.tag);
  const normalizedLabel = normalizeSearchText(label);
  return {
    ...tag,
    tag: label,
    _normalizedLabel: normalizedLabel,
    _suggestionText: normalizeSearchText([label, "tag", tag.count].filter(Boolean).join(" ")),
  };
}

function normalizeShow(show = {}) {
  const title = String(show.title || "");
  const tags = cleanViewerFacingCopyList(
    Array.isArray(show.tasteTags) ? show.tasteTags : [],
    { protectedPhrases: title ? [title] : [] }
  );
  const genres = Array.isArray(show.genres) ? show.genres : [];
  const normalizedTitle = normalizeSearchText(title);
  const normalizedSource = normalizeSearchText(show.source);
  const normalizedMonth = normalizeSearchText([show.monthLabel, show.yearMonth].filter(Boolean).join(" "));
  const normalizedTags = tags.map((tag) => normalizeSearchText(tag)).filter(Boolean);
  const normalizedGenres = genres.map((genre) => normalizeSearchText(genre)).filter(Boolean);
  const normalizedTasteTags = tags.map((tag, indexValue) => ({
    label: tag,
    text: normalizedTags[indexValue],
  }));
  const normalizedGenreEntries = genres.map((genre, indexValue) => ({
    label: genre,
    text: normalizedGenres[indexValue],
  }));
  const searchableParts = [
    title,
    show.source,
    show.monthLabel,
    show.yearMonth,
    show.releaseDate,
    show.summary,
    ...genres,
    ...tags,
  ];
  const all = normalizeSearchText(searchableParts.filter(Boolean).join(" "));

  return {
    ...show,
    title,
    _normalizedLabel: normalizedTitle,
    tasteTags: tags,
    genres,
    _suggestionText: normalizeSearchText([
      title,
      "show",
      show.source,
      show.monthLabel,
      ...tags,
    ].filter(Boolean).join(" ")),
    _search: {
      title: normalizedTitle,
      titleTokens: tokenizeSearchText(normalizedTitle),
      source: normalizedSource,
      sourceTokens: tokenizeSearchText(normalizedSource),
      month: normalizedMonth,
      monthTokens: tokenizeSearchText(normalizedMonth),
      tags: normalizedTags,
      tasteTagEntries: normalizedTasteTags,
      tagTokens: normalizedTags.map(tokenizeSearchText),
      tagSet: new Set(normalizedTags),
      genres: normalizedGenres,
      genreEntries: normalizedGenreEntries,
      genreTokens: normalizedGenres.map(tokenizeSearchText),
      all,
      allTokens: tokenizeSearchText(all),
    },
  };
}

export function normalizeArchiveSearchIndex(index = {}, { scope = ARCHIVE_SEARCH_INDEX_SCOPES.ARCHIVE } = {}) {
  const normalizedScope = normalizeArchiveSearchIndexScope(scope);
  const scopedIndex = filterArchiveSearchIndexForScope(index, normalizedScope);
  const rawShows = Array.isArray(scopedIndex?.shows) ? scopedIndex.shows : [];
  const shows = rawShows.map(normalizeShow);
  const showsBySlug = new Map(shows.map((show) => [show.slug, show]).filter(([slug]) => slug));
  const tags = Array.isArray(scopedIndex?.tags) ? scopedIndex.tags.map(normalizeTag) : [];
  const tagTokens = new Set(tags.flatMap((tag) => tokenizeSearchText(tag.tag)).filter(Boolean));
  const titleTokens = new Set(shows.flatMap((show) => show._search?.titleTokens || []).filter(Boolean));

  return {
    ...scopedIndex,
    scope: normalizedScope,
    shows,
    showsBySlug,
    tags,
    tagsBySlug: buildTagLookup(tags),
    tagTokens,
    titleTokens,
  };
}

function validateArchiveSearchIndex(index, { staticFallback = false } = {}) {
  return Boolean(
    index &&
    typeof index === "object" &&
    (typeof index.corpusVersion === "string" || (staticFallback && index.corpusVersion === null)) &&
    Array.isArray(index.shows) &&
    Array.isArray(index.tags)
  );
}

async function fetchJson(fetchImpl, path) {
  const response = await fetchImpl(path, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }
  return response.json();
}

function getApiIndexPath(scope = ARCHIVE_SEARCH_INDEX_SCOPES.ARCHIVE) {
  return normalizeArchiveSearchIndexScope(scope) === ARCHIVE_SEARCH_INDEX_SCOPES.ALL
    ? `${ARCHIVE_SEARCH_API_INDEX_PATH}?scope=all`
    : ARCHIVE_SEARCH_API_INDEX_PATH;
}

function getStaticIndexPath(scope = ARCHIVE_SEARCH_INDEX_SCOPES.ARCHIVE) {
  return normalizeArchiveSearchIndexScope(scope) === ARCHIVE_SEARCH_INDEX_SCOPES.ALL
    ? ARCHIVE_SEARCH_INDEX_ALL_PATH
    : ARCHIVE_SEARCH_INDEX_PATH;
}

export async function loadArchiveSearchIndex({
  fetchImpl = globalThis.fetch?.bind(globalThis),
  force = false,
  scope = ARCHIVE_SEARCH_INDEX_SCOPES.ARCHIVE,
} = {}) {
  if (!fetchImpl) {
    throw new Error("fetch is not available");
  }
  const normalizedScope = normalizeArchiveSearchIndexScope(scope);
  if (!archiveSearchIndexPromises.has(normalizedScope) || force) {
    const promise = (async () => {
      try {
        const index = await fetchJson(fetchImpl, getApiIndexPath(normalizedScope));
        if (validateArchiveSearchIndex(index)) {
          return normalizeArchiveSearchIndex(index, { scope: normalizedScope });
        }
      } catch {
        // Fall through to the prerendered static index.
      }

      const fallback = await fetchJson(fetchImpl, getStaticIndexPath(normalizedScope));
      if (!validateArchiveSearchIndex(fallback, { staticFallback: true })) {
        throw new Error("Archive search index response is invalid");
      }
      return normalizeArchiveSearchIndex(fallback, { scope: normalizedScope });
    })().catch((error) => {
      archiveSearchIndexPromises.delete(normalizedScope);
      throw error;
    });
    archiveSearchIndexPromises.set(normalizedScope, promise);
  }
  return archiveSearchIndexPromises.get(normalizedScope);
}

export function resetArchiveSearchIndexCache() {
  archiveSearchIndexPromises.clear();
}
